/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef, useState } from "react";
import { Plus, Pencil, Trash2, Search, Shield, Upload, X } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import TablePagination from "@/components/TablePagination";
import {
  useClubs, useClubFilterOptions, useCreateClub, useUpdateClub, useDeleteClub,
} from "@/hooks/api/useClubs";
import type { Club, CreateClubRequest } from "@/api/services/clubs.service";
import { clubsService } from "@/api/services/clubs.service";
import { toast } from "react-toastify";

const emptyForm: CreateClubRequest = {
  name: "",
  shortName: "",
  logoUrl: "",
  country: "",
  league: "",
  founded: undefined,
};

export default function ClubsPage() {
  const [search, setSearch] = useState("");
  const [league, setLeague] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Club | null>(null);
  const [form, setForm] = useState<CreateClubRequest>(emptyForm);
  const [errors, setErrors] = useState<{ name?: string; founded?: string }>({});
  const [logoPreview, setLogoPreview] = useState("");
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useClubs({
    search: search || undefined,
    league,
    country,
    page,
    limit,
  });
  const { data: filterOptions } = useClubFilterOptions();

  const hasActiveFilters = !!search || !!league || !!country;
  const clearFilters = () => {
    setSearch("");
    setLeague(undefined);
    setCountry(undefined);
    setPage(1);
  };
  const { mutate: create, isPending: isCreating } = useCreateClub();
  const { mutate: update, isPending: isUpdating } = useUpdateClub();
  const { mutate: remove } = useDeleteClub();

  const clubs = data?.clubs ?? [];
  const pagination = data?.pagination;

  const set = <K extends keyof CreateClubRequest>(k: K, v: CreateClubRequest[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setLogoPreview("");
    setOpen(true);
  };

  const openEdit = (club: Club) => {
    setEditing(club);
    setForm({
      name: club.name,
      shortName: club.shortName ?? "",
      logoUrl: club.logoUrl ?? "",
      country: club.country ?? "",
      league: club.league ?? "",
      founded: club.founded ?? undefined,
    });
    setLogoPreview(club.logoUrl ?? "");
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setLogoPreview("");
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image size must be less than 2MB"); return; }
    setIsUploadingLogo(true);
    try {
      const res = await clubsService.uploadLogo(file);
      set("logoUrl", res.url);
      setLogoPreview(res.url);
      toast.success("Logo uploaded");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Upload failed");
    } finally {
      setIsUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.name.trim()) newErrors.name = "Club name is required";
    if (form.founded !== undefined && (form.founded < 1800 || form.founded > new Date().getFullYear()))
      newErrors.founded = `Founded year must be between 1800 and ${new Date().getFullYear()}`;
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    setErrors({});

    const payload: CreateClubRequest = {
      name: form.name.trim(),
      ...(form.shortName && { shortName: form.shortName.trim() }),
      ...(form.logoUrl && { logoUrl: form.logoUrl.trim() }),
      ...(form.country && { country: form.country.trim() }),
      ...(form.league && { league: form.league.trim() }),
      ...(form.founded && !Number.isNaN(form.founded) && { founded: Number(form.founded) }),
    };

    if (editing) {
      update(
        { id: editing.id, data: payload },
        {
          onSuccess: () => { toast.success("Club updated"); closeDialog(); },
          onError: (e: any) => toast.error(e.response?.data?.message || "Update failed"),
        },
      );
    } else {
      create(payload, {
        onSuccess: () => { toast.success("Club created"); closeDialog(); },
        onError: (e: any) => toast.error(e.response?.data?.message || "Create failed"),
      });
    }
  };

  const handleDelete = (club: Club) => {
    if (!confirm(`Delete "${club.name}"? This cannot be undone.`)) return;
    remove(club.id, {
      onSuccess: () => toast.success("Club deleted"),
      onError: (e: any) => toast.error(e.response?.data?.message || "Delete failed"),
    });
  };

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Clubs</h1>
          <p className="text-gray-500 mt-1">Manage the canonical club registry</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="mr-2 h-4 w-4" /> Add Club
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search clubs…"
                className="pl-8 max-w-xs"
              />
            </div>

            {/* League filter */}
            <Select
              value={league ?? "all"}
              onValueChange={(val) => {
                setLeague(val === "all" ? undefined : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="League" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Leagues</SelectItem>
                {(filterOptions?.leagues ?? []).map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Country filter */}
            <Select
              value={country ?? "all"}
              onValueChange={(val) => {
                setCountry(val === "all" ? undefined : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {(filterOptions?.countries ?? []).map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Clear filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700 gap-1"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs">
            <Badge className="bg-gray-100 text-gray-700 font-medium">
              {pagination?.total ?? clubs.length} Total
            </Badge>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Club</TableHead>
              <TableHead>Short</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>League</TableHead>
              <TableHead>Founded</TableHead>
              <TableHead>Players</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                  Loading…
                </TableCell>
              </TableRow>
            ) : clubs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                  {hasActiveFilters ? "No clubs match your filters" : "No clubs yet — add the first one"}
                </TableCell>
              </TableRow>
            ) : (
              clubs.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {c.logoUrl ? (
                        <img src={c.logoUrl} alt={c.name} className="w-8 h-8 rounded-full object-cover border" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <Shield className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                      <span className="font-medium text-gray-900">{c.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">{c.shortName || "—"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{c.country || "—"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{c.league || "—"}</TableCell>
                  <TableCell className="text-sm text-gray-600">{c.founded ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{c._count?.players ?? 0}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)}>
                        <Pencil className="h-4 w-4 text-gray-500" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {pagination && pagination.totalPages > 1 && (
          <div className="border-t">
            <TablePagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              pageSize={pagination.limit}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setLimit(s); setPage(1); }}
            />
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeDialog())}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit: ${editing.name}` : "Add New Club"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">Name <span className="text-red-500">*</span></Label>
              <Input
                value={form.name}
                onChange={(e) => { set("name", e.target.value); if (e.target.value.trim()) setErrors((er) => ({ ...er, name: undefined })); }}
                placeholder="FC Barcelona"
                className={errors.name ? "border-red-400" : ""}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1 font-medium">{errors.name}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">Short Name</Label>
                <Input
                  value={form.shortName ?? ""}
                  onChange={(e) => set("shortName", e.target.value)}
                  placeholder="BAR"
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">Founded</Label>
                <Input
                  type="number"
                  min={1800}
                  max={2100}
                  value={form.founded ?? ""}
                  onChange={(e) => { set("founded", e.target.value ? Number(e.target.value) : undefined); setErrors((er) => ({ ...er, founded: undefined })); }}
                  placeholder="1899"
                  className={errors.founded ? "border-red-400" : ""}
                />
                {errors.founded && <p className="text-xs text-red-500 mt-1 font-medium">{errors.founded}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">Country</Label>
                <Input
                  value={form.country ?? ""}
                  onChange={(e) => set("country", e.target.value)}
                  placeholder="Spain"
                />
              </div>
              <div>
                <Label className="mb-1 block text-sm">League</Label>
                <Input
                  value={form.league ?? ""}
                  onChange={(e) => set("league", e.target.value)}
                  placeholder="La Liga"
                />
              </div>
            </div>

            <div>
              <Label className="mb-1 block text-sm">Club Logo</Label>
              {logoPreview ? (
                <div className="flex items-center gap-3 mb-2">
                  <img
                    src={logoPreview}
                    alt="logo preview"
                    className="w-14 h-14 rounded-full object-contain border bg-white"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { set("logoUrl", ""); setLogoPreview(""); }}
                    className="text-red-500 hover:text-red-700 px-2"
                  >
                    <X className="h-4 w-4" />
                    Remove
                  </Button>
                </div>
              ) : null}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isUploadingLogo}
                  onClick={() => logoInputRef.current?.click()}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  <Upload className="h-4 w-4 mr-1" />
                  {isUploadingLogo ? "Uploading…" : "Upload Logo"}
                </Button>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Or paste a URL below</p>
              <Input
                value={form.logoUrl ?? ""}
                onChange={(e) => { set("logoUrl", e.target.value); setLogoPreview(e.target.value); }}
                placeholder="https://…"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
